(ns common.resource.query.advertiser
  (:require ["@sentry/react" :as Sentry]
            [camel-snake-kebab.core :as csk]
            [camel-snake-kebab.extras :as cske]
            [cljs-bean.core :refer [->clj]]
            [common.queries :refer [invalidate-queries query-client]]
            [common.queries.http :as qhttp]
            [common.queries.muix-filter-util :as filter-util]
            [promesa.core :as p]
            [re-frame.core :as rf]
            [reitit.frontend.easy :as r.easy]))

(def entity-name "advertiser")

(defn transform-advertisers [^js data]
  (->> (->clj data)
       (mapv (fn [advertiser]
               (assoc advertiser :campaign-count (count (:campaignIds advertiser)))))))

(defn advertisers-enriched-query
  [{:keys [project-id workspace-id tenant-id token]}]
  #js {:queryKey #js ["advertisers-enriched"]
       :queryFn (fn [_ctx]
                  (qhttp/get
                   (qhttp/render :api/advertisers {} {})
                   {:headers {:Authorization (str "Bearer " token)
                              :X-project-id project-id
                              :X-workspace-id workspace-id
                              :X-tenant-id tenant-id}}))
       :select transform-advertisers
       :staleTime (* 60 1000)})

(defn advertisers-query []
  #js {:queryKey #js ["advertisers"]
       :queryFn (fn [_ctx]
                  (qhttp/get (qhttp/render :api/advertisers {})))
       :select transform-advertisers
       :staleTime (* 60 1000)})

(defn transform-stats [^js data]
  (->> data ->clj (mapv (fn [{:keys [advertiser-id impressions clicks]}]
                          {:id advertiser-id
                           :impressions impressions
                           :clicks clicks
                           :ctr (/ clicks impressions)}))))

(defn advertisers-stats-query [{:keys [agency-id daterange]}]
  #js {:queryKey #js ["advertisers-stats" agency-id daterange]
       :queryFn (fn [_ctx]
                  (qhttp/get (qhttp/render :measure-api/advertiser-stats {}
                                           (->> (merge daterange
                                                       {:agency-id agency-id})
                                                (cske/transform-keys csk/->snake_case)))))
       :select transform-stats
       :staleTime (* 60 1000)})

(defn advertiser-query [{:keys [advertiser-id]}]
  #js {:queryKey #js ["advertiser" advertiser-id]
       :queryFn (fn [_ctx] (qhttp/get (qhttp/render :api/advertiser {:advertiser-id advertiser-id})))
       :enabled (boolean advertiser-id)})

(def ensure-advertiser-controller
  {:parameters {:path [:advertiser-id]}
   :start (fn [{path-params :path}]
            (-> (p/let [data (.ensureQueryData query-client (advertiser-query path-params))
                        advertiser (->clj data)]
                  (.setTag ^js Sentry "advertiser" (:name advertiser))
                  (js/localStorage.setItem "last-viewed-advertiser-id" (:id advertiser))
                  (rf/dispatch [:resource.advertiser/get-one-success advertiser]))
                (p/catch #(r.easy/navigate :common/agency-selection))))})

(defn create-advertiser-mutation []
  #js {:mutationKey #js ["create-advertiser"]
       :mutationFn (fn [payload]
                     (qhttp/post (qhttp/render :api/advertisers {})
                                 {:body payload}))
       :onMutate (fn []
                   (rf/dispatch [:loader/open]))
       :onSuccess (fn []
                    ;; We need to wait a bit otherwise the advertisers resource is not updated yet
                    (js/setTimeout
                     (fn []
                       (rf/dispatch [:loader/close])
                       (invalidate-queries {:queryKey ["advertisers"]})
                       (rf/dispatch [:response-handler/handle-response
                                     {:entity-name entity-name
                                      :type :success
                                      :message "Successfully created advertiser"}]))
                     500))
       :onError (fn []
                  (rf/dispatch [:loader/close])
                  (rf/dispatch [:response-handler/handle-response
                                {:entity-name entity-name
                                 :type :error
                                 :message "Failed to create advertiser!!"}]))})

(defn create-demo-account-mutation [sso-token]
  #js {:mutationKey #js ["create-demo-account"]
       :mutationFn (fn [payload]
                     (qhttp/post (qhttp/render :api/demo-advertisers {})
                                 {:body payload}))
       :onMutate (fn []
                   (rf/dispatch [:loader/open]))
       :onSuccess (fn []
                    ;; We need to wait a bit otherwise the advertisers resource is not updated yet
                    (js/setTimeout
                     (fn []
                       (rf/dispatch [:loader/close])
                       (invalidate-queries {:queryKey ["advertisers"]})
                       (rf/dispatch [:response-handler/handle-response
                                     {:entity-name entity-name
                                      :type :success
                                      :message "Successfully created demo account"}])
                       (rf/dispatch-sync [:common.os/resolve-os-context])
                       (rf/dispatch-sync [:common.resource.sso/sso-login
                                          {:token {:access-token sso-token}
                                           :on-failure nil}])
                       (rf/dispatch [:modal/close :os/request-demo-account]))
                     500))
       :onError (fn []
                  (rf/dispatch [:loader/close])
                  (rf/dispatch [:response-handler/handle-response
                                {:entity-name entity-name
                                 :type :error
                                 :message "Failed to create advertiser!!"}]))})

(defn upgrade-demo-account-mutation [advertiser-id]
  #js {:mutationKey #js ["upgrade-demo-account"]
       :mutationFn (fn [advertiser-id]
                     (qhttp/patch (qhttp/render :api/upgrade-demo-account {:advertiser-id advertiser-id})
                                  {:body {:isDemo false}}))
       :onMutate (fn []
                   (rf/dispatch [:loader/open]))
       :onSuccess (fn []
                    ;; We need to wait a bit otherwise the advertisers resource is not updated yet
                    (js/setTimeout
                     (fn []
                       (rf/dispatch [:loader/close])
                       (invalidate-queries {:queryKey ["advertisers"]})
                       (invalidate-queries {:queryKey ["advertiser" advertiser-id]})
                       (rf/dispatch [:response-handler/handle-response
                                     {:entity-name entity-name
                                      :type :success
                                      :message "Successfully upgraded demo account"}]))
                     500))
       :onError (fn []
                  (rf/dispatch [:loader/close])
                  (rf/dispatch [:response-handler/handle-response
                                {:entity-name entity-name
                                 :type :error
                                 :message "Failed to upgrade advertiser!!"}]))})

(defn copy-pre-made-template-mutation [{:keys [advertiser-id]}]
  #js {:mutationKey #js ["copy-pre-made-template" advertiser-id]
       :mutationFn (fn [{:keys [id]}]
                     (qhttp/post (qhttp/render :api/pre-made-templates-copy-for-advertiser {})
                                 {:body {:parentPreMadeTemplateId id
                                         :advertiserId advertiser-id}}))})

(defn search-integration-errors-query
  [{:keys [advertiser-id] :as context} filter-params
   {:keys [^js sort-model ^js filter-model ^js pagination-model] :as table-models}]
  #js {:enabled (and (some? advertiser-id) (some? pagination-model))
       :queryKey #js ["search-integration-errors"
                      advertiser-id
                      sort-model
                      filter-model
                      (some-> pagination-model .-pageSize)]
       :queryFn (fn [^js ctx]
                  (let [{:keys [muix-data-grid] :as query-params}
                        (update-in (filter-util/get-query-params
                                    filter-params
                                    table-models ctx)
                                   [:muix-data-grid :filters]
                                   (fn [filters]
                                     (map #(update % :value flatten)
                                          filters)))]
                    (qhttp/post (qhttp/render :api/content-build-search-integration-errors
                                              context
                                              (dissoc query-params :muix-data-grid))
                                {:body {:muix-data-grid muix-data-grid}})))
       :getNextPageParam filter-util/default-get-next-page-param
       :staleTime (* 60 1000)})
